import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, resolveImage } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Heart, Truck, RotateCcw, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addToCart, user, refreshUser } = useApp();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [activeImage, setActiveImage] = useState(0);
    const [size, setSize] = useState("");

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        api.get(`/products/${slug}`).then(r => {
            setProduct(r.data);
            setActiveImage(0);
            setSize(r.data.sizes?.[0] || r.data.size || "");
            const cat = r.data.category ? `?category=${encodeURIComponent(r.data.category)}&limit=8` : "?limit=8";
            api.get(`/products${cat}`).then(rr => {
                setRelated(rr.data.filter(p => p.id !== r.data.id).slice(0, 4));
            });
        }).catch(() => setProduct(null));
    }, [slug]);

    if (product === null) return (
        <div className="px-6 md:px-12 lg:px-24 py-32 text-center" data-testid="product-notfound">
            <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)]">Prodotto non trovato</h1>
            <Link to="/shop" className="overline mt-6 inline-block link-underline">TORNA ALLO SHOP</Link>
        </div>
    );
    if (!product) return <div className="px-6 md:px-12 lg:px-24 py-20 text-[color:var(--ms-muted)]">Caricamento…</div>;

    const inWishlist = user?.wishlist?.includes(product.id);
    const toggleWishlist = async () => {
        if (!user) { navigate("/login"); return; }
        if (inWishlist) {
            await api.delete(`/wishlist/${product.id}`);
            toast.success("Rimosso dai preferiti");
        } else {
            await api.post(`/wishlist/${product.id}`);
            toast.success("Aggiunto ai preferiti");
        }
        refreshUser();
    };

    const handleAdd = () => {
        const chosen = size || product.size || "M";
        addToCart(product, chosen, 1);
        toast.success(`Aggiunto al carrello · ${product.title}`);
    };

    const displayPrice = product.on_sale && product.sale_price ? product.sale_price : product.price;
    const images = product.images?.length ? product.images : [{ url: "", alt: "" }];

    const specs = [
        product.age_group && { label: "Età", value: product.age_group },
        product.gender && { label: "Chi indossa", value: capitalize(product.gender) },
        product.color && { label: "Colore", value: product.color },
        product.material && { label: "Materiale", value: product.material },
        product.brand && { label: "Brand", value: product.brand },
        product.season && { label: "Stagione", value: product.season },
        product.fit && { label: "Vestibilità", value: product.fit },
        product.condition && { label: "Stato", value: product.condition },
    ].filter(Boolean);

    return (
        <div className="px-6 md:px-12 lg:px-24 py-10" data-testid={`product-page-${product.id}`}>
            <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-8 lg:gap-14">
                {/* Images */}
                <div className="space-y-3 w-full max-w-[380px] mx-auto lg:mx-0">
                    <div className="relative aspect-[3/4] bg-[color:var(--ms-cream)] overflow-hidden" data-testid="product-main-image">
                        {images[activeImage]?.url ? (
                            <img src={resolveImage(images[activeImage].url)} alt={product.title} className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-[color:var(--ms-muted)]">Nessuna immagine</div>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className="grid grid-cols-5 gap-2">
                            {images.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImage(i)}
                                    className={`aspect-[3/4] bg-[color:var(--ms-cream)] overflow-hidden border-2 ${i === activeImage ? "border-[color:var(--ms-sage)]" : "border-transparent"}`}
                                    data-testid={`product-thumb-${i}`}
                                >
                                    <img src={resolveImage(img.url)} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="lg:sticky lg:top-32 self-start">
                    {product.brand && <div className="overline mb-3" data-testid="product-brand">{product.brand}</div>}
                    <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[color:var(--ms-charcoal)] leading-tight" data-testid="product-title">{product.title}</h1>
                    <div className="flex items-baseline gap-3 mt-3" data-testid="product-price">
                        <span className={`text-2xl font-medium ${product.on_sale && product.sale_price ? "text-[color:var(--ms-terracotta)]" : "text-[color:var(--ms-charcoal)]"}`}>€{displayPrice.toFixed(2)}</span>
                        {product.on_sale && product.sale_price && (
                            <span className="text-[color:var(--ms-muted)] line-through text-sm">€{product.price.toFixed(2)}</span>
                        )}
                    </div>
                    {product.age_group && <div className="mt-1 text-sm text-[color:var(--ms-sage-dark)]">Consigliato per: <span className="font-medium">{product.age_group}</span></div>}

                    {/* Size selector */}
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-2">
                            <div className="overline">Taglia</div>
                            <Link to="/taglie" className="overline text-[color:var(--ms-sage-dark)] link-underline">GUIDA TAGLIE</Link>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {(product.sizes?.length ? product.sizes : [product.size || "M"]).map(s => (
                                <button
                                    key={s}
                                    onClick={() => setSize(s)}
                                    className={`px-5 py-3 text-sm border ${s === size ? "border-[color:var(--ms-charcoal)] bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)]" : "border-[color:var(--ms-border)] text-[color:var(--ms-charcoal)]"} hover:border-[color:var(--ms-charcoal)] transition-colors min-w-[3rem]`}
                                    data-testid={`size-${s}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 grid grid-cols-[1fr_auto] gap-2">
                        <button
                            onClick={handleAdd}
                            disabled={product.stock <= 0}
                            className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] py-4 text-xs tracking-[0.22em] font-medium hover:bg-[color:var(--ms-sage-dark)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="product-add-cart"
                        >
                            {product.stock > 0 ? `AGGIUNGI AL CARRELLO · €${displayPrice.toFixed(2)}` : "ESAURITO"}
                        </button>
                        <button onClick={toggleWishlist} className={`border px-4 ${inWishlist ? "border-[color:var(--ms-terracotta)] text-[color:var(--ms-terracotta)]" : "border-[color:var(--ms-border)] text-[color:var(--ms-charcoal)]"} hover:border-[color:var(--ms-terracotta)] transition-colors`} data-testid="product-wishlist">
                            <Heart size={16} fill={inWishlist ? "currentColor" : "none"} />
                        </button>
                    </div>

                    <div className="mt-8 space-y-4 border-t border-[color:var(--ms-border)] pt-8 text-sm text-[color:var(--ms-charcoal)] leading-relaxed" data-testid="product-description">
                        {product.description && (
                            <p className="text-[color:var(--ms-charcoal)] text-base font-light">{product.description}</p>
                        )}
                        {specs.length > 0 && (
                            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs pt-2">
                                {specs.map(s => (
                                    <div key={s.label}>
                                        <dt className="overline mb-0.5">{s.label}</dt>
                                        <dd className="text-[color:var(--ms-charcoal)]">{s.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                        {Object.keys(product.measurements || {}).length > 0 && (
                            <div className="pt-2">
                                <div className="overline mb-2">Misure</div>
                                <ul className="text-xs text-[color:var(--ms-charcoal)] space-y-1">
                                    {Object.entries(product.measurements).map(([k, v]) => <li key={k}><span className="text-[color:var(--ms-muted)] capitalize">{k}:</span> {v}</li>)}
                                </ul>
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[color:var(--ms-border)]">
                            <div className="text-xs text-[color:var(--ms-muted)] flex flex-col items-start gap-1.5">
                                <Truck size={16} className="text-[color:var(--ms-sage-dark)]" /> <span>Spedizione 24/48h</span>
                            </div>
                            <div className="text-xs text-[color:var(--ms-muted)] flex flex-col items-start gap-1.5">
                                <RotateCcw size={16} className="text-[color:var(--ms-sage-dark)]" /> <span>Reso 14 giorni</span>
                            </div>
                            <div className="text-xs text-[color:var(--ms-muted)] flex flex-col items-start gap-1.5">
                                <ShieldCheck size={16} className="text-[color:var(--ms-sage-dark)]" /> <span>Pagamento sicuro</span>
                            </div>
                        </div>
                        {product.shipping_info && <p className="text-xs text-[color:var(--ms-muted)] pt-2">{product.shipping_info}</p>}
                    </div>
                </div>
            </div>

            {related.length > 0 && (
                <section className="mt-24 border-t border-[color:var(--ms-border)] pt-16">
                    <div className="mb-10">
                        <div className="overline mb-3">Potrebbero piacerti</div>
                        <h2 className="font-display text-3xl text-[color:var(--ms-charcoal)]">Altri capi selezionati per te</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10">
                        {related.map(p => <ProductCard key={p.id} product={p} />)}
                    </div>
                </section>
            )}
        </div>
    );
}

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
